import React, { useContext, useState } from 'react';
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Typography,
  Paper,
  Fade,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL,Notification_Url } from '../../Api/ApiUrls';
import { AuthContext } from './../../AuthContext/AuthContext';
import { generateToken } from '../../Firebase/Firebase';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SecurityIcon from '@mui/icons-material/Security';
import ComputerIcon from '@mui/icons-material/Computer';

const AdminForceLoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { identifier, fcmToken } = location.state || {};
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    setLoading(true);
    try {
      // Ensure we still have a token
      const finalFcmToken = fcmToken || (await generateToken());

      const response = await fetch(`${BASE_URL}/force-login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: identifier,
          fcm_token: finalFcmToken || "",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Admin force login success:", data);

        login(data.user_id, data.session_token);

        // Store admin session
        sessionStorage.setItem('isAuthenticated', 'true');
        sessionStorage.setItem('user_id', data.user_id);
        sessionStorage.setItem('session_token', data.session_token);
        sessionStorage.setItem('isAdmin', 'true');

        if (finalFcmToken) {
          try {
            const notifyResponse = await fetch(`${Notification_Url}/send-notification`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                token: finalFcmToken,
                title: "Admin Login - LandNest",
                body: "You have successfully logged in as administrator.",
              }),
            });

            const notifyData = await notifyResponse.json();
            if (notifyResponse.ok) {
              console.log("Admin notification sent successfully:", notifyData);
            } else {
              console.error("Failed to send admin notification:", notifyData);
            }
          } catch (notifyError) {
            console.error("Error sending admin notification:", notifyError);
          }
        } else {
          console.warn("No FCM token available for notification.");
        }

        navigate("/admin-customers");
      } else {
        console.error("Admin force login failed:", data);
      }
    } catch (err) {
      console.error("Error in admin force login:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={700}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '200%',
            height: '200%',
            top: '-50%',
            left: '-50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '30px 30px',
            transform: 'rotate(30deg)',
            animation: 'moveBackground 120s linear infinite',
          },
          '@keyframes moveBackground': {
            '0%': { transform: 'rotate(30deg) translateX(0)' },
            '100%': { transform: 'rotate(30deg) translateX(-30px)' },
          }
        }}
      >
        <Container maxWidth="md">
          <CssBaseline />
          <Paper
            elevation={24}
            sx={{
              p: 5,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid rgba(25, 118, 210, 0.3)',
              backdropFilter: 'blur(10px)',
              textAlign: 'center',
              color: '#1976d2',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            }}
          >
            {/* Decorative elements */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 120,
                height: 120,
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
                opacity: 0.1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: 100,
                height: 100,
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                clipPath: 'polygon(0 100%, 0 0, 100% 100%)',
                opacity: 0.1,
              }}
            />
            
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                bgcolor: '#1976d2',
                boxShadow: '0 0 20px rgba(25, 118, 210, 0.5)',
              }}
            >
              <AdminPanelSettingsIcon sx={{ fontSize: 40 }} />
            </Avatar>

            <Chip
              icon={<SecurityIcon />}
              label="Admin Portal"
              color="primary"
              variant="outlined"
              sx={{ mb: 3, fontWeight: 'bold', px: 1 }}
            />
            
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#1976d2', 
                fontWeight: 700, 
                mb: 2,
                fontSize: { xs: '1.5rem', md: '2.125rem' }
              }}
            >
              Administrator Session Conflict
            </Typography>
            
            <Divider sx={{ my: 3, borderColor: 'rgba(25, 118, 210, 0.3)' }} />
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
              <ComputerIcon sx={{ mr: 2, color: '#f57c00' }} />
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#555', 
                  textAlign: 'left',
                  fontSize: { xs: '0.9rem', md: '1rem' }
                }}
              >
                Your administrator account is already active on another device. 
                Continuing here will log out the other session for security reasons.
              </Typography>
            </Box>

            <Box sx={{ 
              backgroundColor: 'rgba(25, 118, 210, 0.05)', 
              p: 2, 
              borderRadius: 2,
              mb: 4,
              border: '1px dashed rgba(25, 118, 210, 0.3)'
            }}>
              <Typography variant="body2" sx={{ color: '#1976d2', fontStyle: 'italic' }}>
                Administrator ID: {identifier || 'Not provided'}
              </Typography>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              justifyContent: 'center',
              gap: 3,
              mt: 4
            }}>
              <Button
                onClick={handleContinue}
                variant="contained"
                disabled={loading}
                sx={{
                  background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                  color: '#fff',
                  fontWeight: 'bold',
                  px: 5,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0, #1976d2)',
                    boxShadow: '0 6px 12px rgba(25, 118, 210, 0.4)',
                  },
                  minWidth: 180
                }}
              >
                {loading ? 'Processing...' : 'Continue as Admin'}
              </Button>
              
              <Button
                onClick={() => navigate("/login")}
                variant="outlined"
                sx={{
                  color: '#1976d2',
                  borderColor: '#1976d2',
                  px: 5,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  },
                  minWidth: 180
                }}
              >
                Back to Login
              </Button>
            </Box>
            
            <Typography variant="caption" display="block" sx={{ mt: 4, color: '#777' }}>
              For security reasons, only one active admin session is allowed at a time.
            </Typography>
          </Paper>
        </Container>
      </Box>
    </Fade>
  );
};

export default AdminForceLoginPage;